import { useEffect, useRef, useState } from 'react';
import { api, ApiFailure } from '../lib/api';

interface Candidate {
  address: string;
  lat: number;
  lng: number;
}

/**
 * 按地址搜坐标。
 *
 * ★ 建单页上**唯一能当场拿到坐标的办法**。异步地理编码是建完单之后才跑的,
 *   而且编不出来时只会静静地把单丢进复核队列 —— 那时候电话早挂了。
 *
 * ★ 摆一串候选让人挑,而不是直接把第一条填进去。
 *   人在电话里说的地址通常是残缺的(「Queen Street 那个」),
 *   编码服务对残缺输入照样会给一个自信的答案,而它经常在隔壁镇。
 *   挑一下是发现这件事的唯一时机。
 */
export function AddressSearchBox({
  query,
  onPick,
}: {
  query: string;
  onPick: (c: Candidate) => void;
}) {
  const [list, setList] = useState<Candidate[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  /** 已经搜过的那串字 —— 同一串不重复打接口 */
  const searched = useRef('');

  /*
   * ★ 边打边搜,而不是让人再点一次按钮。
   *
   *   原来只有一个「按这个地址搜坐标」的链接 —— 而客服的直觉是
   *   「我地址都填了,候选怎么不出来」,于是那颗按钮**没人点**,
   *   单子就这么建出去了:没有坐标 → 进复核队列 → 上不了地图、
   *   司机导航不了,而那时候电话早挂了。
   *
   * ★ 但地理编码是**按次收费**的,所以不能每敲一个字母打一次:
   *   · 停手 700 毫秒才发
   *   · 少于 6 个字不发(「Queen」这种搜出来的是隔壁镇)
   *   · 同一串字不重复发(选中候选之后地址框会被填成候选的原文,
   *     不挡住的话它会立刻再搜一次自己)
   *   算下来一个地址大约一次,和人点一下按钮是一个量级。
   */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 6 || q === searched.current) return;
    const t = setTimeout(() => void run(), 700);
    return () => clearTimeout(t);
    // run 每次渲染都是新函数,放进依赖会让定时器一直被重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  async function run() {
    const q = query.trim();
    if (q.length < 2) {
      setReason('先填几个字的地址再搜');
      setList([]);
      return;
    }
    setBusy(true);
    setReason(null);
    searched.current = q;
    try {
      const r = await api<{ data: Candidate[]; reason: string | null }>(
        `/geo/search?q=${encodeURIComponent(q)}`,
      );
      setList(r.data);
      setReason(r.reason);
    } catch (e) {
      setList([]);
      // ★ 说明白是「没配」还是「没搜到」—— 前者要去设置页,后者要换写法
      setReason(e instanceof ApiFailure ? e.message : '地址搜索失败');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        className="text-xs text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-400"
      >
        {busy ? '搜索中…' : '重新搜一次坐标'}
      </button>

      {reason && <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">{reason}</p>}

      {list && list.length > 0 && (
        <div className="mt-1 rounded-lg border border-slate-200 dark:border-slate-600">
          {list.map((c, i) => (
            <button
              key={`${c.lat},${c.lng},${i}`}
              type="button"
              onClick={() => {
                /*
                 * ★ 先记下「这串字搜过了」再回调。
                 *   回调会把地址框填成候选的原文,而那次改动
                 *   会再触发一轮搜索 —— 搜的是它自己。
                 */
                searched.current = c.address.trim();
                onPick(c);
                setList(null);
              }}
              className="block w-full px-2 py-1.5 text-left text-xs hover:bg-slate-50
                         dark:hover:bg-slate-700"
            >
              <span className="text-slate-700 dark:text-slate-200">{c.address}</span>
              <span className="ml-2 font-mono text-slate-400">
                {c.lat.toFixed(5)}, {c.lng.toFixed(5)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
